import { z } from 'zod'

export const LEARNING_CATALOG_READ_CONTRACT_VERSION = '1.0.0' as const

const catalogNodeTypeSchema = z.enum([
  'category',
  'program',
  'course',
  'bundle',
  'module',
  'coaching_product',
  'spa_artifact',
  'export_package',
])
const catalogNodeStatusSchema = z.enum(['draft', 'active', 'archived'])
const catalogRelationshipTypeSchema = z.enum([
  'contains',
  'grants',
  'maps_to',
  'requires',
  'part_of',
  'replaces',
  'equivalent_to',
])
const catalogEdgeStatusSchema = z.enum(['active', 'inactive', 'draft'])
const catalogMappingKindSchema = z.enum([
  'same_as',
  'source_for',
  'derived_from',
  'delivery_target',
  'export_package',
])
const catalogMappingConfidenceSchema = z.enum([
  'manual_confirmed',
  'import_exact',
  'import_fuzzy',
  'ambiguous',
])
const catalogMappingStatusSchema = z.enum(['active', 'inactive', 'ambiguous', 'rejected'])
const jsonObjectSchema = z.record(z.unknown())

/** Full catalog-node projection. Passthrough preserves additive legacy fields. */
export const learningCatalogNodeSchema = z.object({
  id: z.string().min(1),
  nodeType: catalogNodeTypeSchema,
  slug: z.string().min(1),
  title: z.string(),
  description: z.string().nullable(),
  status: catalogNodeStatusSchema,
  version: z.string().nullable(),
  metadataJson: jsonObjectSchema,
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
}).passthrough()

/** Full catalog-edge projection. Passthrough preserves additive legacy fields. */
export const learningCatalogEdgeSchema = z.object({
  id: z.string().min(1),
  fromNodeId: z.string().min(1),
  toNodeId: z.string().min(1),
  relationshipType: catalogRelationshipTypeSchema,
  orderIndex: z.number().int().nullable(),
  effectiveFrom: z.string().nullable(),
  effectiveTo: z.string().nullable(),
  status: catalogEdgeStatusSchema,
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
}).passthrough()

/** Full catalog-mapping projection. Passthrough preserves additive legacy fields. */
export const learningCatalogMappingSchema = z.object({
  id: z.string().min(1),
  catalogNodeId: z.string().min(1),
  externalEntityId: z.string().min(1),
  mappingKind: catalogMappingKindSchema,
  confidence: catalogMappingConfidenceSchema,
  mappingStatus: catalogMappingStatusSchema,
  ownerRole: z.string(),
  notes: z.string().nullable(),
  createdBy: z.string().min(1),
  updatedBy: z.string().min(1),
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
}).passthrough()

const listEnvelope = z.object({
  contract_version: z.literal(LEARNING_CATALOG_READ_CONTRACT_VERSION),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  total: z.number().int().nonnegative(),
  nextCursor: z.string().nullable(),
}).passthrough()

export const learningCatalogNodeListResponseSchema = listEnvelope.extend({
  items: z.array(learningCatalogNodeSchema),
})
export const learningCatalogNodeDetailResponseSchema = z.object({
  contract_version: z.literal(LEARNING_CATALOG_READ_CONTRACT_VERSION),
  item: learningCatalogNodeSchema,
}).passthrough()
export const learningCatalogEdgeListResponseSchema = listEnvelope.extend({
  items: z.array(learningCatalogEdgeSchema),
})
export const learningCatalogEdgeDetailResponseSchema = z.object({
  contract_version: z.literal(LEARNING_CATALOG_READ_CONTRACT_VERSION),
  item: learningCatalogEdgeSchema,
}).passthrough()
export const learningCatalogMappingListResponseSchema = listEnvelope.extend({
  items: z.array(learningCatalogMappingSchema),
})
export const learningCatalogMappingDetailResponseSchema = z.object({
  contract_version: z.literal(LEARNING_CATALOG_READ_CONTRACT_VERSION),
  item: learningCatalogMappingSchema,
}).passthrough()

export type LearningCatalogNode = z.infer<typeof learningCatalogNodeSchema>
export type LearningCatalogNodeListResponse = z.infer<typeof learningCatalogNodeListResponseSchema>
export type LearningCatalogNodeDetailResponse = z.infer<typeof learningCatalogNodeDetailResponseSchema>
export type LearningCatalogEdge = z.infer<typeof learningCatalogEdgeSchema>
export type LearningCatalogEdgeListResponse = z.infer<typeof learningCatalogEdgeListResponseSchema>
export type LearningCatalogEdgeDetailResponse = z.infer<typeof learningCatalogEdgeDetailResponseSchema>
export type LearningCatalogMapping = z.infer<typeof learningCatalogMappingSchema>
export type LearningCatalogMappingListResponse = z.infer<typeof learningCatalogMappingListResponseSchema>
export type LearningCatalogMappingDetailResponse = z.infer<typeof learningCatalogMappingDetailResponseSchema>
